/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useMemo } from 'react';

import { jsx } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithCondition } from '@atlaskit/platform-feature-flags-react';
import { RovoInlineChatPrompt } from '@atlassian/conversation-assistant-chat-prompt-input';

import type { EditorPluginAIProvider } from '../../../../types/types';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';

const providerProductToProuductKey = (product: EditorPluginAIProvider['product']) => {
	switch (product) {
		case 'CONFLUENCE':
			return 'confluence';
		case 'JSW':
		case 'JSM':
		case 'JWM':
		case 'JPD':
			return 'jira';
		case 'ATLAS':
		case 'TOWNSQUARE':
			return 'atlas';
		case 'BITBUCKET':
			return 'bitbucket';
		default:
			return 'unknown';
	}
};

const backupChannelVariables = {
	cloudId: '',
	orgId: undefined,
};

interface RovoPromptScreenWithLogicProps {
	cancel: (options?: { shouldClearSelectionContext?: boolean }) => void;
}

function RovoPromptScreenWithLogicNew({ cancel }: RovoPromptScreenWithLogicProps) {
	const ai = useAIExperienceCommonDataContext();
	const { editorPluginAIProvider, setCancelRovoPrompt } = ai;
	const { setIsRovoLoading, api } = useContentComponentContext();

	const { cloudId, orgId } = useMemo(
		() => ai.editorPluginAIProvider?.getChannelVariables?.('rovo-agent') || backupChannelVariables,
		[ai.editorPluginAIProvider],
	);
	const product = ai.editorPluginAIProvider.product;
	const productKey = providerProductToProuductKey(product);

	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);

	useEffect(() => {
		setCancelRovoPrompt(undefined);
		return () => {
			setIsRovoLoading(false);
		};
	}, [setCancelRovoPrompt, setIsRovoLoading]);

	const handleAbort = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			cancel({ shouldClearSelectionContext: streamEnded });
			setIsRovoLoading(false);
		},
		[cancel, setIsRovoLoading],
	);

	const onSubmit = useCallback(
		(cancel: () => void) => {
			editorPluginAIProvider.onSubmit?.();
			setIsRovoLoading(true);

			const cancelRovoPrompt = () => {
				cancel();
			};
			// give DOM time to update the screen
			setTimeout(() => {
				setCancelRovoPrompt(() => cancelRovoPrompt);
			}, 100);
		},
		[editorPluginAIProvider, setCancelRovoPrompt, setIsRovoLoading],
	);

	// If a session is active - we'll show the loading screen elsewhere and we can hide this
	if (nextSessionId && fg('platform_editor_ai_aifc_patch_beta_2')) {
		return null;
	}

	return (
		<FlagsProvider>
			<RovoInlineChatPrompt
				source={productKey === 'confluence' ? 'CONFLUENCE_INLINE_EDITOR' : 'INLINE_EDITOR'}
				productKey={productKey}
				siteId={cloudId}
				orgId={orgId}
				objectId={ai.editorPluginAIProvider.objectId}
				onAbort={handleAbort}
				onCancel={() => {
					/* noop - this is to allow the rovo inline chat to manage cancel behaviour.
					 * We shouldn't end the AI experience directly. */
				}}
				// The following nops usages are like this because we want to ensure that only the AI plugin or the Orchestrator
				// are controlling the xstate. We do not want this to end the ai experience directly. Instead the rovo bridge
				// will call the orchestrator which will then end the experience, on both complete and abort.
				// IMPORTANT!! Do not end the AI experience on the complete callback. This can cause problems because the
				// rovo bridge may still have events to stream in the queue, and it's possible that this could result in
				// the experience being ended immediately on mount, which would then unmount the prompt. This is because the rovo bridge
				// can have a final response in its queue which has never fluushed previously, because something else unmounted this
				// component before the final response was flushed.
				onComplete={() => {
					setIsRovoLoading(false);
				}}
				onSubmit={onSubmit}
				enableAgents={editorPluginAIProvider.isRovoEnabled && fg('platform_inline_rovo_agents')}
				_showLabsLozenge
			/>
		</FlagsProvider>
	);
}

function RovoPromptScreenWithLogicOld({ cancel }: RovoPromptScreenWithLogicProps) {
	const ai = useAIExperienceCommonDataContext();
	const { editorPluginAIProvider, setCancelRovoPrompt } = ai;
	const { setIsRovoLoading, api } = useContentComponentContext();

	const siteId = useMemo(
		() => ai.editorPluginAIProvider?.getChannelVariables?.('rovo-agent')?.cloudId || '',
		[ai.editorPluginAIProvider],
	);
	const product = ai.editorPluginAIProvider.product;
	const productKey = providerProductToProuductKey(product);

	const nextSessionId = useSharedPluginStateWithSelector(
		api,
		['aiExperience'],
		({ aiExperienceState }) => aiExperienceState?.aiDecorationExperiencePluginState.nextSessionId,
	);

	useEffect(() => {
		setCancelRovoPrompt(undefined);
		return () => {
			setIsRovoLoading(false);
		};
	}, [setCancelRovoPrompt, setIsRovoLoading]);

	const handleAbort = useCallback(
		({ streamEnded }: { streamEnded: boolean }) => {
			cancel({ shouldClearSelectionContext: streamEnded });
			setIsRovoLoading(false);
		},
		[cancel, setIsRovoLoading],
	);

	const onSubmit = useCallback(
		(cancel: () => void) => {
			editorPluginAIProvider.onSubmit?.();
			setIsRovoLoading(true);

			const cancelRovoPrompt = () => {
				cancel();
			};
			// give DOM time to update the screen
			setTimeout(() => {
				setCancelRovoPrompt(() => cancelRovoPrompt);
			}, 100);
		},
		[editorPluginAIProvider, setCancelRovoPrompt, setIsRovoLoading],
	);

	// If a session is active - we'll show the loading screen elsewhere and we can hide this
	if (nextSessionId && fg('platform_editor_ai_aifc_patch_beta_2')) {
		return null;
	}

	return (
		<FlagsProvider>
			<RovoInlineChatPrompt
				source={productKey === 'confluence' ? 'CONFLUENCE_INLINE_EDITOR' : ''}
				productKey={productKey}
				siteId={siteId}
				// Follow-up: Retrieve the orgId - might need to be a prop or from context
				orgId={''}
				objectId={ai.editorPluginAIProvider.objectId}
				onAbort={handleAbort}
				onCancel={() => {
					/* noop - this is to allow the rovo inline chat to manage cancel behaviour.
					 * We shouldn't end the AI experience directly. */
				}}
				// The following nops usages are like this because we want to ensure that only the AI plugin or the Orchestrator
				// are controlling the xstate. We do not want this to end the ai experience directly. Instead the rovo bridge
				// will call the orchestrator which will then end the experience, on both complete and abort.
				// IMPORTANT!! Do not end the AI experience on the complete callback. This can cause problems because the
				// rovo bridge may still have events to stream in the queue, and it's possible that this could result in
				// the experience being ended immediately on mount, which would then unmount the prompt. This is because the rovo bridge
				// can have a final response in its queue which has never fluushed previously, because something else unmounted this
				// component before the final response was flushed.
				onComplete={() => {
					setIsRovoLoading(false);
				}}
				onSubmit={onSubmit}
				enableAgents={editorPluginAIProvider.isRovoEnabled && fg('platform_inline_rovo_agents')}
				_showLabsLozenge
			/>
		</FlagsProvider>
	);
}

export const RovoPromptScreenWithLogic = componentWithCondition(
	() => fg('platform_editor_ai_pass_orgid_to_chat_prompt'),
	RovoPromptScreenWithLogicNew,
	RovoPromptScreenWithLogicOld,
);
